import { Store } from "../uidispatcherapi/Store";
import Tracking from "../uidispatcherapi/Tracking";
import { SessionStore } from "../uidispatcherapi/SessionStore";
import { reloadPage } from "../utils/locationUtil";
const MIN_STABLE_UI_VERSION_URL = "/ordercapture/uidispatcher/rest/min-stable-ui-version";
// Random time period for check from 3-5 minutes to avoid synced reloads
const CHECK_PERIOD = 3 * 60 * 1000 + Math.floor(Math.random() * 2 * 60 * 1000);
const ALERT_TIMEOUT = 15 * 1000;
const APP_USER_AGENT = "Companion";
type MinimumStableUIVersion = {
	timestampUtc: string | null | undefined;
};
export class ForceReloadStore extends Store {
	_state: {
		reloadPending: boolean;
	};

	constructor() {
		super();
		this._state = {
			reloadPending: false,
		};
	}

	async checkForForceReload() {
		// Don't check for force reload in M.Companion app
		const isApp = navigator.userAgent.includes(APP_USER_AGENT);
		if (isApp) {
			return;
		}
		// Check immediately, whether the local timestamp differs from the server config,
		// and don't wait several minutes for the first setInterval duration before force reloading.
		let currentTimestamp: string | null | undefined = await this.handleNewTimestamp(
			SessionStore.getLastKnownForceReloadTimestamp(),
			true,
		);
		setInterval(async () => {
			currentTimestamp = await this.handleNewTimestamp(currentTimestamp, false);
		}, CHECK_PERIOD);
	}

	async handleNewTimestamp(
		currentTimestamp: string | null | undefined,
		instantForceReload: boolean,
	): Promise<string | null | undefined> {
		const newTimestamp: string | null | undefined = await this.fetchTimestamp();

		if (newTimestamp === currentTimestamp || !newTimestamp) {
			return currentTimestamp;
		}

		if (newTimestamp && !currentTimestamp) {
			return newTimestamp;
		}
		// The page will be force reloaded only if there are differences in timestamps.
		// For the M.Companion iOS app, forcing a reload on the first visit (when localStorage is empty) can cause issues.
		// If WKWebView receives two identical requests and the first one is not yet finished (the page isn't fully loaded),
		// it will cancel the first request and send the second one, cancelling all ongoing requests on the page (such as chunk loadings or openid-configuration).
		// This results in a broken page in the app.

		Tracking.trackChangingMinStableUIVersion(currentTimestamp, newTimestamp);
		SessionStore.setLastKnownForceReloadTimestamp(newTimestamp);

		if (instantForceReload) {
			reloadPage();
		} else {
			this._state = {
				reloadPending: true,
			};
			this.trigger();
			setTimeout(() => {
				reloadPage();
			}, ALERT_TIMEOUT);
		}

		return newTimestamp;
	}

	async fetchTimestamp(): Promise<string | null | undefined> {
		try {
			const newVersionResponse: Response = await fetch(MIN_STABLE_UI_VERSION_URL, {
				method: "get",
				credentials: "omit",
				headers: {
					CallTreeId: SessionStore.generateSessionedCallTreeIdWithPrefix("PLAT"),
				},
			});

			if (!newVersionResponse.ok) {
				Tracking.trackError(
					"platform",
					"ui",
					"MinStableUIVersion",
					"Unsuccessful min stable UI version response: " + JSON.stringify(newVersionResponse),
					null,
				);
				console.error(
					"Fetching min stable UI version has unsuccessful status. Received: " +
						JSON.stringify({
							url: newVersionResponse.url,
							status: newVersionResponse.status,
						}),
				);
				return null;
			}

			const responseJson: Promise<MinimumStableUIVersion> | null | undefined = newVersionResponse
				? newVersionResponse.json()
				: null;
			return responseJson ? responseJson.then(newVersion => (newVersion ? newVersion.timestampUtc : null)) : null;
		} catch (error) {
			Tracking.trackError("platform", "ui", "MinStableUIVersion", "Fetching min stable UI version failed", error);
			console.error("Fetching min stable UI version failed: ", error);
			return null;
		}
	}

	isReloadPending(): boolean {
		return this._state.reloadPending;
	}
}
